import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { CalendarCheck, MapPin, Compass, BadgeCheck, ArrowRight, Briefcase } from "lucide-react";
import { MaskedLine } from "@/components/motion/Reveal";
import { scrollToId } from "@/lib/scroll";

const BADGES = [
  { icon: CalendarCheck, text: "Oltre 30 Anni di Esperienza (dal 1994)" },
  { icon: MapPin, text: "Presenza Nazionale — Napoli · Milano" },
  { icon: Compass, text: "Approccio Strategico & Su Misura" },
  { icon: BadgeCheck, text: "Massima Trasparenza Tariffaria" },
];

const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 200]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0.35]);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-navy" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute -inset-y-24 inset-x-0 lg:left-[38%]">
        <img
          src="/brand/hero-bg.jpg"
          alt="Logo Studio Legale Avv. Giuseppe Napolitano"
          className="h-full w-full object-cover object-[center_30%] opacity-40 lg:opacity-85"
          data-testid="hero-bg-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/40 to-navy" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-transparent lg:via-navy/60" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(197,155,39,0.14),transparent_55%)]" />

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-24 w-full">
        <MaskedLine delay={0.15}>
          <span className="inline-flex items-center gap-3 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.35em] text-gold">
            <span className="h-px w-10 bg-gold/70" />
            Studio Legale Avv. Giuseppe Napolitano
          </span>
        </MaskedLine>

        <h1 className="mt-6 font-serif text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight" data-testid="hero-headline">
          <MaskedLine delay={0.35}>Eccellenza e <span className="font-display italic text-gold">Tutela Legale</span></MaskedLine>
          <MaskedLine delay={0.5}>su Scala Nazionale.</MaskedLine>
        </h1>

        <MaskedLine delay={0.75}>
          <p className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300" data-testid="hero-subheadline">
            Oltre 30 anni di esperienza, rigore tecnico e visione strategica al servizio di privati ed imprese.
          </p>
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollToId("#contatti")}
            data-testid="hero-cta-consultation"
            className="group inline-flex items-center gap-3 bg-gold px-8 py-4 text-sm font-semibold uppercase tracking-wider text-navy shadow-[0_0_35px_rgba(197,155,39,0.35)] hover:bg-gold-light transition-colors"
          >
            Richiedi una Consulenza
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            onClick={() => scrollToId("#sedi")}
            data-testid="hero-cta-sedi"
            className="inline-flex items-center gap-3 border border-white/25 px-8 py-4 text-sm font-semibold uppercase tracking-wider text-white hover:border-gold hover:text-gold transition-colors"
          >
            Le Nostre Sedi
          </button>
          <Link
            to="/lavora-con-noi"
            data-testid="hero-cta-careers"
            className="group inline-flex items-center gap-3 border border-gold/60 px-8 py-4 text-sm font-semibold uppercase tracking-wider text-gold hover:bg-gold hover:text-navy transition-colors"
          >
            <Briefcase className="h-4 w-4" /> Lavora con noi
          </Link>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10" data-testid="hero-badges">
          {BADGES.map((b, i) => (
            <motion.div
              key={b.text}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="flex items-center gap-3 bg-navy/80 backdrop-blur-sm px-5 py-4"
            >
              <b.icon className="h-5 w-5 shrink-0 text-gold" />
              <span className="text-xs sm:text-sm font-medium leading-snug text-slate-200">{b.text}</span>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-slate-400">Scorri</span>
        <motion.span
          animate={{ scaleY: [0.3, 1, 0.3] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="block h-10 w-px origin-top bg-gold/70"
        />
      </motion.div>
    </section>
  );
};

export default Hero;
